import { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';

const Navbar = () => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 30);
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const scrollTo = (id: string) => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
        setIsOpen(false);
    };

    const navLinks = [
        { label: t('nav.services'), id: 'services' },
        { label: t('nav.about'), id: 'about' },
        { label: t('nav.portfolio'), id: 'portfolio' },
        { label: t('nav.contact'), id: 'contact' },
    ];

    return (
        <nav className={`fixed z-50 transition-all duration-300 ${scrolled ? 'top-2 left-2 right-2' : 'top-4 left-4 right-4'}`}>
            <div className={`max-w-6xl mx-auto px-5 py-3 rounded-2xl flex items-center justify-between transition-all duration-300 ${scrolled ? 'bg-slate-900/95 backdrop-blur-md shadow-2xl shadow-black/40 border border-white/10' : 'bg-slate-900/60 backdrop-blur-md border border-white/10'}`}>
                {/* Logo */}
                <button
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    className="font-bold text-xl text-white cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 rounded"
                    aria-label="Go to top"
                >
                    <span className="text-blue-400">Fin</span>fraTech
                </button>

                {/* Desktop nav links */}
                <div className="hidden md:flex items-center gap-7">
                    {navLinks.map(link => (
                        <button
                            key={link.id}
                            onClick={() => scrollTo(link.id)}
                            className="text-gray-300 hover:text-white text-sm font-medium transition-colors duration-200 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 rounded"
                        >
                            {link.label}
                        </button>
                    ))}
                </div>

                {/* Right side */}
                <div className="hidden md:flex items-center gap-4">
                    <LanguageSwitcher />
                    <button
                        onClick={() => scrollTo('contact')}
                        className="bg-blue-600 hover:bg-blue-500 text-white px-5 py-2 rounded-xl text-sm font-semibold transition-all duration-200 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
                    >
                        {t('nav.getInTouch')}
                    </button>
                </div>

                {/* Mobile: language + hamburger */}
                <div className="md:hidden flex items-center gap-3">
                    <LanguageSwitcher />
                    <button
                        onClick={() => setIsOpen(!isOpen)}
                        className="text-white p-1 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 rounded"
                        aria-label="Toggle menu"
                        aria-expanded={isOpen}
                    >
                        {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                    </button>
                </div>
            </div>

            {/* Mobile dropdown */}
            {isOpen && (
                <div className="md:hidden mt-2 max-w-6xl mx-auto bg-slate-900/95 backdrop-blur-md rounded-2xl border border-white/10 px-6 py-5 flex flex-col gap-4">
                    {navLinks.map(link => (
                        <button
                            key={link.id}
                            onClick={() => scrollTo(link.id)}
                            className="text-gray-300 hover:text-white text-left text-sm font-medium transition-colors cursor-pointer focus:outline-none"
                        >
                            {link.label}
                        </button>
                    ))}
                    <button
                        onClick={() => scrollTo('contact')}
                        className="bg-blue-600 hover:bg-blue-500 text-white px-5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 cursor-pointer w-full text-center mt-1"
                    >
                        {t('nav.getInTouch')}
                    </button>
                </div>
            )}
        </nav>
    );
};

export default Navbar;
